import { inject, Injectable, InjectionToken } from '@angular/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { Observable, retry, timer } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';

/** A single server-sent event, reduced to what consumers need. */
export interface SseFrame {
  event: string;
  data: string;
}

export type SseTransport = typeof fetchEventSource;

export const SSE_TRANSPORT = new InjectionToken<SseTransport>('sse-transport', {
  providedIn: 'root',
  factory: () => fetchEventSource,
});

const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;

const EVENT_STREAM_CONTENT_TYPE = 'text/event-stream';

export class SseConnectionError extends Error {
  constructor(
    message: string,
    readonly fatal: boolean,
  ) {
    super(message);
    this.name = 'SseConnectionError';
  }
}

/**
 * Thin wrapper around `@microsoft/fetch-event-source` that adds what a long-lived authenticated
 * stream needs: a fresh bearer token per connection attempt, exponential backoff, and a teardown
 * that actually aborts the request.
 */
@Injectable({
  providedIn: 'root',
})
export class SseClient {
  private readonly transport = inject(SSE_TRANSPORT);
  private readonly authenticationService = inject(AuthenticationService);

  connect(url: string): Observable<SseFrame> {
    return new Observable<SseFrame>((subscriber) => {
      const abortController = new AbortController();

      this.transport(url, {
        signal: abortController.signal,
        // Keep receiving while the tab is in the background
        openWhenHidden: true,
        headers: {
          Authorization: `Bearer ${this.authenticationService.getAccessToken()}`,
        },
        // Replaces the library default, which only looks at the content type and therefore reports
        // a 403 as "expected content-type text/event-stream, actual: null" — the least useful
        // sentence available. A stream that never opens is otherwise completely silent.
        onopen: (response) => {
          assertStreamOpened(url, response);
          return Promise.resolve();
        },
        onmessage: (message) => subscriber.next({ event: message.event, data: message.data }),
        // Rethrowing takes the retry decision away from the library and hands it to the `retry`
        // operator below. That matters for auth: every attempt re-runs this factory and therefore
        // reads a current access token, instead of replaying the one the stream opened with.
        onerror: (error) => {
          throw error;
        },
        onclose: () => {
          throw new Error(`SSE stream closed by the server: ${url}`);
        },
      })
        .then(() => subscriber.complete())
        .catch((error: unknown) => subscriber.error(error));

      return () => abortController.abort();
    }).pipe(
      retry({
        delay: (error: unknown, retryCount) => {
          if (error instanceof SseConnectionError && error.fatal) {
            console.error(`SSE: giving up on ${url}.`, error.message);
            throw error;
          }

          console.warn(`SSE: ${url} dropped, attempt ${retryCount} follows.`, error);
          return timer(retryDelayMs(retryCount));
        },
        resetOnSuccess: true,
      }),
    );
  }
}

/**
 * A 401 is worth retrying — the next attempt reads a fresh token. A 403 or 404 is not.
 *
 * A 2xx that isn't an event stream is retryable too: that is what a proxy in front of the backend
 * hands back when it hiccups, and giving up on it kills live updates for the rest of the session.
 */
function assertStreamOpened(url: string, response: Response): void {
  const contentType = response.headers.get('content-type');

  if (response.ok && contentType?.startsWith(EVENT_STREAM_CONTENT_TYPE)) {
    return;
  }

  const detail = `HTTP ${response.status}, content-type ${contentType ?? 'none'}`;

  throw new SseConnectionError(
    `SSE stream ${url} did not open: ${detail}`,
    isFatalStatus(response.status),
  );
}

function isFatalStatus(status: number): boolean {
  const retryable = status === 401 || status === 408 || status === 429;

  return status >= 400 && status < 500 && !retryable;
}

function retryDelayMs(retryCount: number): number {
  return Math.min(INITIAL_RETRY_DELAY_MS * 2 ** (retryCount - 1), MAX_RETRY_DELAY_MS);
}
